import { TableSessionRequest } from '@api/models';
import { inject, Injectable, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { SweetAlertService } from '@core/services/sweet-alert.service';
import { ClientTableSessionService } from '@features/client/services/client-table-session-service';
import { ErrorHandlerService } from '@core/services/error-handler.service';
import { NavigationService } from '@core/services/navigation.service';
import { SessionUtils } from '@core/utils/session-utils';
import { AuthService } from '@features/auth/services/auth-service';
import { AuthResponse } from '@core/models/auth.model';
import { HttpErrorLike } from '@core/models/http-error-like.model';
import { logger } from '@core/utils/logger';

/** Lo que manda el QR (o el código corto) para entrar a la mesa. */
export type TableAccessRequest = TableSessionRequest;

@Injectable({
  providedIn: 'root',
})
export class QrProcessingService {
  private authService = inject(AuthService);
  private sweetAlertService = inject(SweetAlertService);
  private clientTableSessionService = inject(ClientTableSessionService);
  private errorHandler = inject(ErrorHandlerService);
  private navigation = inject(NavigationService);

  private isSubmitting = signal(false);
  public isInQrFlow = signal(false);

  processTableRequest(request: TableAccessRequest): void {
    this.isInQrFlow.set(true);
    const current = this.authService.tableSessionId();

    if (SessionUtils.isValidSession(current)) {
      this.sweetAlertService.showInfo(
        'Sesión activa',
        'Ya estás participando en una mesa. No es posible escanear otra.',
      );
      this.navigation.navigateToHome();
      return;
    }

    if (this.isSubmitting()) return;

    if (this.authService.isAuthenticated()) {
      this.processAuthenticatedUser(request);
    } else {
      this.processGuestFlow(request);
    }
  }

  private processAuthenticatedUser(request: TableAccessRequest): void {
    this.isSubmitting.set(true);
    this.sweetAlertService.showLoading('Validando mesa...', 'Conectando con la mesa');

    this.authService
      .scanQR(request)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (response: AuthResponse) => {
          if (!response?.tableNumber || !response?.activeParticipants) {
            this.handleInvalidResponse();
            return;
          }

          const nickname = this.getNicknameFromResponse(response);

          this.clientTableSessionService.setTableSessionInfo(
            response.tableNumber,
            nickname,
            response.numberOfParticipants || 0,
            response.tableCapacity ?? null,
            response.activeParticipants,
            response.previousParticipants,
            this.hostFrom(response),
          );

          this.sweetAlertService.showSuccess(
            `¡Bienvenido ${nickname}!`,
            `Te has unido a la mesa ${response.tableNumber}`,
          );
          this.navigation.navigateToHome();
        },
        error: (err) => this.handleScanError(err),
      });
  }

  private processGuestFlow(request: TableAccessRequest): void {
    this.sweetAlertService
      .showChoice(
        '¿Cómo querés continuar?',
        'Podés ingresar como invitado o iniciar sesión.',
        'Seguir como invitado',
        'Iniciar sesión',
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.askGuestName(request);
        } else {
          this.authService.setPendingTableScan(JSON.stringify(request));
          this.navigation.navigateToLogin();
        }
      });
  }

  private askGuestName(request: TableAccessRequest): void {
    this.sweetAlertService
      .inputText('Ingresá tu nombre', 'Este nombre aparecerá en la mesa', 'Nombre del invitado')
      .then((nickname) => {
        if (nickname === null) {
          this.navigation.navigateToHome();
          return;
        }

        this.finishGuestScan(request, nickname.trim());
      });
  }

  private finishGuestScan(request: TableAccessRequest, nickname: string): void {
    this.isSubmitting.set(true);
    this.sweetAlertService.showLoading('Uniéndote...', 'Conectando con la mesa');

    this.authService
      .scanQR({ ...request, nickname })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (response: AuthResponse) => {
          this.clientTableSessionService.setTableSessionInfo(
            response.tableNumber!,
            nickname,
            response.numberOfParticipants || 0,
            response.tableCapacity ?? null,
            response.activeParticipants,
            response.previousParticipants,
            this.hostFrom(response),
          );
          // Si no es el anfitrión, la respuesta no dice quién es: se pide la mesa al back (a los
          // clientes logueados ya se la pide ClientTableSessionService al entrar)
          if (!response.isHostClient) {
            this.clientTableSessionService.recoverActiveSession().subscribe({
              error: (err) => logger.warn('Mesa', 'No se pudo cargar el anfitrión de la mesa', err),
            });
          }
          this.sweetAlertService.showSuccess(
            `¡Bienvenido ${nickname}!`,
            `Te has unido a la mesa ${response.tableNumber}`,
          );
          this.navigation.navigateToHome();
        },
        error: (err) => this.handleScanError(err),
      });
  }

  private getNicknameFromResponse(response: AuthResponse): string {
    const participantId = this.authService.participantId();
    const actives = response.activeParticipants ?? [];
    const previous = response.previousParticipants ?? [];

    const current =
      actives.find((p) => p.publicId === participantId) ||
      previous.find((p) => p.publicId === participantId);

    // ✅ Lógica simplificada: El back ya nos manda el nickname procesado
    // (sea el nombre del usuario o el nickname elegido como invitado)
    const nickname = current?.nickname;

    return this.toSafeNickname(nickname, participantId);
  }

  private toSafeNickname(nickname: string | undefined, participantId: string | null): string {
    const fallback = `Invitado-${(participantId ?? '').slice(-4) || '0000'}`;
    if (!nickname) return fallback;

    if (nickname.toLowerCase().startsWith('guest')) {
      return nickname.replace(/^Guest/i, 'Invitado');
    }
    return nickname;
  }

  private handleScanError(errorResponse: HttpErrorLike): void {
    this.isSubmitting.set(false);
    this.sweetAlertService.closeAll();
    const { title, message } = this.errorHandler.getQrScanError(errorResponse);
    this.sweetAlertService.showError(title, message).then(() => this.navigation.navigateToHome());
  }

  /** La respuesta de entrar a la mesa dice si el participante es el anfitrión, no quién es. */
  private hostFrom(response: AuthResponse): string | null {
    return response.isHostClient ? this.authService.participantId() : null;
  }

  private handleInvalidResponse(): void {
    this.isSubmitting.set(false);
    this.sweetAlertService.showError('Error', 'La respuesta del servidor no es válida');
  }
}
